<template>
  <div class="container-fluid">
    <!-- Info Box at the absolute top level -->
    <InfoBox 
      title="Template Usage Guide"
      description="This is the templating tool for quickly applying parameter changes to audiences.

The template includes parameters to apply, their values, and priorities, which you can set manually. After changing any value in the template, you must finalize the update by saving changes.

Important! If changes are not saved, the previous state will be applied when you hit apply. Whenever you save the template, it will increment the version of the template.

Once you saved your changes, you can apply the parameter changes to selected audiences. The latest applications of the templates can be seen on the side panel.

You can also undo the latest change applied to the selected audiences. This will return the previous states of the parameters that were changed by the apply operation of the template.

Important! You can use undo, only to the latest version of the template that was applied. For instance, if you applied the version 4 of the template to audience A, and then saved new changes for template, which makes it version 5 now, the application of the template version 4 for the audience A cannot be reversed by undo operation. This is to prevent any confusion about what was applied and what changes are reverted. So you can think of it as a failsafe for situations where you applied wrong values and need a quick fix.

For other situations, you can create a new template and apply it to overwrite your previous changes for those audiences."
    />

    <div class="row">
      <div class="col-md-8">
        <div class="template-edit">
          <div class="form-container" style="width: 100%;">
          <div v-if="loading" class="loading-container">
            <div class="spinner"></div>
          </div>

          <div v-else-if="error" class="error-message">
            {{ error }}
          </div>
          
          <div v-else class="form-content">
            <!-- Template Details -->
            <h1 class="form-title">{{ isNew ? 'Create New Template' : 'Edit Template' }}</h1>
            <div class="form-group">
              <label>Version: {{ template.version }}</label>
           
            </div>
            <div class="form-group">
              <label>Name:</label>
              <input
                v-model="template.name"
                type="text"
                class="form-input"
                placeholder="Enter template name"
              />
            </div>

            <div class="form-group">
              <label>Description:</label>
              <textarea
                v-model="template.description"
                class="form-input"
                rows="3"
                placeholder="Enter template description"
              ></textarea>
            </div>

            <!-- Parameters Section -->
            <div class="form-group">
              <label>Parameters:</label>
              <div v-if="template.parameters && template.parameters.length > 0" class="parameters-list">
                <div v-for="(param, index) in template.parameters" :key="index" class="parameter-item">
                  <div class="parameter-header">
                    <span class="parameter-name">{{ param.key }}</span>
                    <div class="parameter-actions">
                      <div class="priority-control">
                        <label>Priority:</label>
                        <input
                          type="number"
                          v-model.number="param.priority"
                          min="1"
                          class="priority-input"
                          disabled
                        />
                      </div>
                      <button @click="editParameter(index)" class="edit-btn">
                        <i class="fas fa-edit"></i>
                      </button>
                      <button @click="removeParameter(index)" class="remove-btn">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                  <div class="parameter-description">{{ param.description }}</div>
                  <div class="parameter-value-container">
                    <input
                      v-model="param.value"
                      type="text"
                      class="form-input"
                      :placeholder="'Enter value for ' + param.key"
                      @input="handleParameterValueChange($event, index)"
                    />
                    <div v-if="param.validationError" class="validation-error">
                      {{ param.validationError }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="no-parameters">
                No parameters added yet
              </div>
              <button @click="openAddParameterModal" class="add-btn">
                Add Parameter
              </button>
            </div>

            <div class="form-actions">
              <button @click="handleDiscard" class="btn-secondary">
                Close
              </button>
              <button 
                @click="handleSave" 
                :disabled="saveLoading" 
                class="btn-primary"
              >
                {{ isNew ? 'Create template' : 'Save changes' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Add Parameter Modal -->
        <div v-if="showAddParameterModal" class="modal-overlay">
          <div class="modal-content">
            <h2 class="modal-title">Add Parameter</h2>
            <div class="form-group">
              <label>Select Parameter:</label>
              <select
                v-model="selectedParameter"
                class="form-input"
              >
                <option value="">Select a parameter</option>
                <option
                  v-for="param in availableParameters"
                  :key="param.id"
                  :value="param.id"
                  :disabled="isParameterSelected(param.id)"
                >
                  {{ param.key }}
                </option>
              </select>
            </div>
            <div v-if="selectedParameter" class="form-group">
              <label>Value:</label>
              <div class="value-input-container">
                <div class="input-type-toggle">
                  <button 
                    class="toggle-btn" 
                    :class="{ active: !isJsonInput }"
                    @click="isJsonInput = false"
                  >
                    Text
                  </button>
                  <button 
                    class="toggle-btn" 
                    :class="{ active: isJsonInput }"
                    @click="isJsonInput = true"
                  >
                    JSON
                  </button>
                </div>
                <textarea
                  v-if="!isJsonInput"
                  v-model="parameterValue"
                  class="form-input rich-text-input"
                  :placeholder="'Enter value for ' + getParameterName(selectedParameter)"
                  rows="4"
                ></textarea>
                <div v-else class="json-generator-container">
                  <json-modal
                    :value-object="parameterValue"
                    title="Edit JSON"
                    :direct-open="isJsonInput"
                    @json-submitted="handleJsonUpdate"
                    :parameterName="getParameterName(selectedParameter)"
                    :editMode="true"
                    :useLegacySave="true"
                  />
                </div>
              </div>
            </div>
            <div v-if="selectedParameter" class="form-group">
              <label>Priority:</label>
              <input
                type="number"
                v-model.number="parameterPriority"
                min="1"
                max="10"
                class="form-input"
                placeholder="Enter priority (1-10)"
              />
            </div>
            <div class="modal-actions">
              <button @click="showAddParameterModal = false" class="btn-secondary">
                Cancel
              </button>
              <button
                @click="addParameter"
                :disabled="!selectedParameter || !parameterValue"
                class="btn-primary"
              >
                Add
              </button>
            </div>
          </div>
        </div>      

        <!-- Warning Modal -->
        <div v-if="showWarningModal" class="modal-overlay">
          <div class="modal-content">
            <h2 class="modal-title">Warning: Existing Parameter Values</h2>
            <div class="warning-content">
              <p>The following parameters already have values for the selected audience(s):</p>
              <div class="warning-list">
                <div v-for="(value, index) in existingValues" :key="index" class="warning-item">
                  <div class="warning-header">
                    <strong>{{ value.parameter }}</strong> in <strong>{{ value.audience }}</strong>
                  </div>
                  <div class="warning-details">
                    <div>Current value: {{ value.existingValue }}</div>
                    <div>New value: {{ value.newValue }}</div>
                  </div>
                </div>
              </div>
              <p class="warning-message">Do you want to proceed with applying the template?</p>
            </div>
            <div class="modal-actions">
              <button @click="cancelApplyTemplate" class="btn-secondary">
                Cancel
              </button>
              <button @click="proceedWithApplyTemplate" class="btn-primary">
                Proceed
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-4">
      <div class="template-edit">
      <div class="form-container">
        <h1 class="form-title">{{ isNew ? 'Start From an Audience' : 'Audience Actions' }}</h1>

        <!-- Prefill from an existing audience (new templates only). Lives here rather than in
             the main column so the side column carries its weight before the template exists —
             Apply/Undo and Active Applications are both edit-only. -->
        <div v-if="isNew" class="form-group copy-from-audience">
          <label>Copy parameters from an audience:</label>
          <div class="copy-from-audience-controls">
            <select v-model="sourceAudience" class="form-input">
              <option value="">Select an audience</option>
              <option v-for="audience in audiences" :key="audience.id" :value="audience.id">
                {{ audience.name }}
              </option>
            </select>
            <button @click="copyFromAudience(sourceAudience)" :disabled="!sourceAudience" class="add-btn">
              Load
            </button>
          </div>
          <div v-if="copyFromAudienceMessage" class="copy-from-audience-message">
            {{ copyFromAudienceMessage }}
          </div>
        </div>

        <div v-if="!isNew" class="form-group">
          <label>Audience:</label>
          <enhanced-autocomplete
            :value="selectedAudience"
            :search="searchAudiences"
            :is-multi-select="true"
            :field-type="'audience'"
            placeholder="Search audiences..."
            @input="handleAudienceSelection"
            class="form-input"
          />
        </div>
        <div class="action-buttons" v-if="!isNew && selectedAudience.length > 0">
          <button 
            @click="applyTemplate" 
            class="btn btn-sm btn-primary"
            :disabled="applyLoading"
          >
            {{ applyLoading ? 'Applying...' : 'Apply Template' }}
          </button>
          <button 
            @click="undoTemplate" 
            class="btn btn-sm btn-secondary"
            :disabled="undoLoading"
          >
            {{ undoLoading ? 'Undoing...' : 'Undo Template' }}
          </button>
        </div>
        <div v-if="actionError" class="error-message">
          {{ actionError }}
        </div>

        <!-- Template Applications Section -->
        <div v-if="!isNew" class="applications-section">
          <h2 class="section-title">Active Applications</h2>
          <div v-if="applicationsLoading" class="loading-container">
            <div class="spinner"></div>
          </div>
          <div v-else-if="applicationsError" class="error-message">
            {{ applicationsError }}
          </div>
          <div v-else-if="activeApplications.length === 0" class="no-applications">
            No active applications found
          </div>
          <div v-else class="applications-list">
            <div v-for="application in activeApplications" :key="application.id" class="application-item">
              <div class="application-header">
                <span class="application-audience">{{ getAudienceName(application.audience_id) }}</span>
                <span class="application-version">Version: {{ application.version }}</span>
                <span class="application-time">{{ formatDate(application.appliedAt) }}</span>
              </div>
              <div class="application-details">
                <div class="parameter-count">
                  <span>Number of parameters: {{ application.parameters.length }}</span>
                </div>
                
                <div class="parameter-count">
                  Parameters: 
                  <span v-for="(param,index) in application.parameters" :key="param.id">
                    {{ param.key }}{{ index < application.parameters.length - 1 ? ', ' : '' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>  
    </div>
    </div>
  </div>
</template>

<script>
import { managerService } from '@/services/manager.service';
import { templateService } from '@/services/templateService';
import EnhancedAutocomplete from '@/components/EnhancedAutocomplete.vue';
import JsonModal from '@/components/common/JsonModal.vue';
import InfoBox from '@/components/common/InfoBox.vue';
import { buildTemplateParameters } from '@/util/templateFromAudience';
import Vue from 'vue';

export default {
  name: 'TemplateEdit',
  components: {
    EnhancedAutocomplete,
    JsonModal,
    InfoBox
  },
  props: {
    id: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      saveLoading: false,
      applyLoading: false,
      undoLoading: false,
      actionError: null,
      showAddParameterModal: false,
      showWarningModal: false,
      existingValues: [],
      template: {
        name: '',
        description: '',
        parameters: []
      },
      selectedParameter: null,
      sourceAudience: '',
      copyFromAudienceMessage: '',
      parameterValue: '',
      selectedAudience: [],
      audienceMap: {},
      isJsonInput: false,
      editingParameterIndex: null,
      parameterPriority: 1,
      applicationsLoading: false,
      applicationsError: null,
      templateApplications: []
    };
  },
  computed: {
    isNew() {
      return !this.id;
    },
    appId() {
      return this.$store.getters['app/activeApp'];
    },
    templates() {
      return this.$store.getters.templates || [];
    },
    loading() {
      return this.$store.getters.isLoading('template');
    },
    availableParameters() {
      return this.$store.getters.parameters || [];
    },
    audiences() {
      const audiences = this.$store.state.audiences || [];
      this.audienceMap = audiences.reduce((acc, audience) => {
        acc[audience.name] = audience.id;
        return acc;
      }, {});
      return audiences;
    },
    activeApplications() {
      if (!this.templateApplications) return [];
      const temp = this.templateApplications.filter(app => app.isActive);
      return temp.sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt));
    }
  },
  methods: {
    async fetchTemplate() {
      if (this.isNew) return;
      
      try {
        const template = this.templates.find(t => t.id === this.id);
        if (template) {
          this.template = { 
            ...template,
            parameters: template.parameters.map(param => ({
              ...param,
              value: this.stringifyValue(param.value)
            }))
          };
          // Fetch applications after template is set
          await this.fetchTemplateApplications();
        } else {
          this.error = 'Template not found';
        }
      } catch (err) {
        this.error = 'Failed to fetch template. Please try again.';
        console.error(err);
      }
    },
    formatResultsForAlert(results, actionType, audiences) {
      // Format operation results for display in alerts
      const formattedResults = results.map((result, index) => {
        const audienceName = audiences[index];
        if (result && typeof result === 'object') {
          const details = [];
          if (result.parameters) {
            details.push(`Parameters ${actionType === 'apply' ? 'applied' : 'undone'}: ${result.parameters.length}`);
          }
          if (result.status) {
            details.push(`Status: ${result.status}`);
          }
          if (result.message) {
            details.push(`Message: ${result.message}`);
          }
          return `Audience: ${audienceName}\n${details.join('\n')}`;
        }
        return `Audience: ${audienceName}\nResult: ${JSON.stringify(result, null, 2)}`;
      }).join('\n\n');

      // Check if any result has an error status
      const hasErrors = results.some(result => 
        result && typeof result === 'object' && result.status === 'error');
      
      // Return the formatted message and status
      return {
        message: `Template ${hasErrors ? 
          (actionType === 'apply' ? 'couldnt be applied' : 'couldnt be undone') : 
          (actionType === 'apply' ? 'applied successfully' : 'undone successfully')
        } to ${results.length} audience(s):\n\n${formattedResults}`,
        status: hasErrors ? 'Warning' : 'Success'
      };
    },
    formatErrorForAlert(err, actionType) {
      const errorMessage = err.response && err.response.data 
        ? JSON.stringify(err.response.data, null, 2)
        : err.message || `Failed to ${actionType} template. Please try again.`;
      
      return {
        title: 'Error',
        message: `Failed to ${actionType} template: ${errorMessage}`,
        type: 'error'
      };
    },
    stringifyValue(value) {
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
      }
      return value;
    },
    validateJson(value) {
      try {
        if (value.trim().startsWith('{') || value.trim().startsWith('[')) {
          JSON.parse(value);
          return true;
        }
        return true; // Not JSON, but valid as string
      } catch (err) {
        return false;
      }
    },
    handleParameterValueChange(event, index) {
      const value = event.target.value;
      const param = this.template.parameters[index];
      
      if (value.trim().startsWith('{') || value.trim().startsWith('[')) {
        if (!this.validateJson(value)) {
          param.validationError = 'Invalid JSON format';
        } else {
          param.validationError = null;
        }
      } else {
        param.validationError = null;
      }
    },
    isParameterSelected(parameterId) {
      return this.template.parameters.some(p => p.id === parameterId);
    },
    getParameterName(parameterId) {
      const param = this.availableParameters.find(p => p.id === parameterId);
      return param?.key || '';
    },
    handleJsonUpdate(json) {
      if (!this.validateJson(json)) {
        this.$alert(JSON.stringify({
          title: 'Error',
          message: 'Invalid JSON format. Please check your input.',
          type: 'error'
        }));
        return;
      }
      this.parameterValue = json;
    },
    openAddParameterModal() {
      this.selectedParameter = null;
      this.parameterValue = '';
      this.isJsonInput = false;
      this.editingParameterIndex = null;
      this.parameterPriority = 1;
      this.showAddParameterModal = true;
    },
    addParameter() {
      if (!this.selectedParameter || !this.parameterValue) return;
      
      if (this.isJsonInput || (this.parameterValue.trim().startsWith('{') || this.parameterValue.trim().startsWith('['))) {
        if (!this.validateJson(this.parameterValue)) {
          this.$alert(JSON.stringify({
            title: 'Error',
            message: 'Invalid JSON format. Please check your input.',
            type: 'error'
          }));
          return;
        }
      }
      
      let value = this.parameterValue;
      try {
        if (this.isJsonInput || (value.trim().startsWith('{') || value.trim().startsWith('['))) {
          value = JSON.parse(value);
        }
      } catch (err) {
        console.warn('Using raw string value:', err);
      }
      
      const param = this.availableParameters.find(p => p.id === this.selectedParameter);
      if (!param) return;
      
      const parameterData = {
        ...param,
        value: this.stringifyValue(value),
        priority: this.parameterPriority || 1
      };
      
      if (this.editingParameterIndex !== null) {
        this.template.parameters[this.editingParameterIndex] = parameterData;
        this.editingParameterIndex = null;
      } else {
        this.template.parameters.push(parameterData);
      }
      
      this.showAddParameterModal = false;
      this.selectedParameter = null;
      this.parameterValue = '';
      this.parameterPriority = 1;
      this.isJsonInput = false;
    },
    copyFromAudience(audienceId) {
      if (!audienceId) return;
      const copied = buildTemplateParameters(this.availableParameters, audienceId);
      const audience = this.audiences.find(a => a.id === audienceId);
      const name = audience ? audience.name : audienceId;
      this.sourceAudience = audienceId;

      // Leave the form untouched on an empty result — an empty template saves cleanly and is
      // indistinguishable from a real one later, so say why nothing happened instead.
      if (!copied.length) {
        this.copyFromAudienceMessage = `"${name}" has no parameter overrides to copy.`;
        return;
      }

      // Loading replaces the list wholesale, so anything added by hand would go silently.
      // Never fires on the hand-off from the audience page: the form is empty at mount.
      if (this.template.parameters.length &&
          !confirm(`This replaces the ${this.template.parameters.length} parameter(s) already on this template with ${copied.length} from "${name}". Continue?`)) {
        return;
      }

      this.template.parameters = copied;
      this.copyFromAudienceMessage =
        `Loaded ${copied.length} parameter${copied.length === 1 ? '' : 's'} from "${name}".`;
    },
    removeParameter(index) {
      this.template.parameters.splice(index, 1);
    },
    editParameter(index) {
      const param = this.template.parameters[index];
      this.selectedParameter = param.id;
      this.parameterValue = param.value;
      this.parameterPriority = param.priority || 1;
      this.isJsonInput = this.isJsonValue(param.value);
      this.editingParameterIndex = index;
      this.showAddParameterModal = true;
    },
    isJsonValue(value) {
      if (!value) return false;
      try {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            JSON.parse(trimmed);
            return true;
          }
        }
        return false;
      } catch (err) {
        return false;
      }
    },
    async handleSave() {
      if (!this.appId) {
        this.error = 'Application ID is required';
        return;
      }

      this.saveLoading = true;
      try {
        const formattedParameters = this.template.parameters.map(param => ({
          id: param.id,
          key: param.key,
          value: this.parseValue(param.value),
          priority: param.priority || 1
        }));

        const templateData = {
          name: this.template.name,
          description: this.template.description,
          parameters: formattedParameters
        };

        // Only add update_time for new templates
        if (this.isNew) {
          templateData.update_time = Date.now();
        }

        if (this.isNew) {
          await managerService.configAdd('template', templateData, { aid: this.appId });
        } else {
          await managerService.configEdit('template', { 
            id: this.id,
            data: templateData
          }, { aid: this.appId });
        }
        
        await this.$store.dispatch('configFetch', 'template');
        this.$alert(
          'Template saved successfully',
          'Success'
        );
        this.$router.push(`/${this.appId}/templates`);
      } catch (err) {
        this.error = 'Failed to save template. Please try again.';
        console.error('Save template error:', err);
        this.$alert(
          this.formatErrorForAlert(err, 'save').message,
          'Error'
        );
      } finally {
        this.saveLoading = false;
      }
    },
    parseValue(value) {
      try {
        if (typeof value === 'string' && (value.trim().startsWith('{') || value.trim().startsWith('['))) {
          return JSON.parse(value);
        }
      } catch (err) {
        console.warn('Using raw string value:', err);
      }
      return value;
    },
    handleDiscard() {
      if (confirm('Are you sure you want to discard changes?')) {
        this.$router.push(`/${this.appId}/templates`);
      }
    },
    async searchAudiences(query) {
      const normalizedQuery = query.toLowerCase();
      return this.audiences
        .filter(audience => 
          audience.name.toLowerCase().includes(normalizedQuery))
        .map(audience => audience.name);
    },
    handleAudienceSelection(value) {
      if (typeof value === 'string') {
        this.selectedAudience = value.includes(',') ? value.split(',').map(item => item.trim()).filter(Boolean) : [value];
      } else if (Array.isArray(value)) {
        this.selectedAudience = value;
      } else {
        this.selectedAudience = [];
      }
    },
    getAudienceId(audienceName) {
      return this.audienceMap[audienceName];
    },
    async applyTemplate() {
      if (this.selectedAudience.length === 0) return;
      
      this.applyLoading = true;
      this.actionError = null;
      
      try {
        // Check for existing values first
        this.existingValues = [];
        for (const audienceName of this.selectedAudience) {
          const audienceId = this.getAudienceId(audienceName);
          if (!audienceId) {
            throw new Error('Selected audience "' + audienceName + '" not found');
          }

          // Check each parameter in the template against existing values
          for (const templateParam of this.template.parameters) {
            const existingParam = this.availableParameters.find(p => p.id === templateParam.id);
            if (existingParam && existingParam.audience_values && existingParam.audience_values[audienceId]) {
              this.existingValues.push({
                audience: audienceName,
                parameter: templateParam.key,
                existingValue: existingParam.audience_values[audienceId],
                newValue: templateParam.value
              });
            }
          }
        }

        // If there are existing values, show warning modal
        if (this.existingValues.length > 0) {
          this.showWarningModal = true;
          this.applyLoading = false;
          return;
        }

        await this.proceedWithApplyTemplate();
      } catch (err) {
        const errorMessage = err.response && err.response.data 
          ? JSON.stringify(err.response.data, null, 2)
          : err.message || 'Failed to apply template. Please try again.';
        
        this.actionError = 'Error: ' + errorMessage;
        console.error('Template application error:', errorMessage);
        
        this.$alert(JSON.stringify(this.formatErrorForAlert(err, 'apply')));
        this.applyLoading = false;
      }
    },
    async proceedWithApplyTemplate() {
      this.applyLoading = true;
      try {
        const results = await Promise.all(
          this.selectedAudience.map(async (audienceName) => {
            const audienceId = this.getAudienceId(audienceName);
            if (!audienceId) {
              throw new Error('Selected audience "' + audienceName + '" not found');
            }
            return templateService.applyTemplate(
              this.appId,
              this.id,
              audienceId
            );
          })
        );
        
        console.log('Template apply results:', results);

        const formattedResults = this.formatResultsForAlert(results, 'apply', this.selectedAudience);

        this.$alert(
          formattedResults.message,
          formattedResults.status
        );

        // Refresh applications list after successful application
        await this.fetchTemplateApplications();
      } catch (err) {
        const errorMessage = err.response && err.response.data 
          ? JSON.stringify(err.response.data, null, 2)
          : err.message || 'Failed to apply template. Please try again.';
        
        this.actionError = 'Error: ' + errorMessage;
        console.error('Template application error:', errorMessage);
        
        this.$alert(JSON.stringify(this.formatErrorForAlert(err, 'apply')));
      } finally {
        this.applyLoading = false;
        this.showWarningModal = false;
        this.existingValues = [];
      }
    },
    cancelApplyTemplate() {
      this.showWarningModal = false;
      this.applyLoading = false;
      this.existingValues = [];
    },
    async undoTemplate() {
      if (this.selectedAudience.length === 0) return;
      
      this.undoLoading = true;
      this.actionError = null;
      
      try {
        const results = await Promise.all(
          this.selectedAudience.map(async (audienceName) => {
            const audienceId = this.getAudienceId(audienceName);
            if (!audienceId) {
              throw new Error('Selected audience "' + audienceName + '" not found');
            }
            return templateService.undoTemplate(
              this.appId,
              this.id,
              audienceId
            );
          })
        );
        
        console.log('Template undo results:', results);

        const formattedResults = this.formatResultsForAlert(results, 'undo', this.selectedAudience);

        this.$alert(
          formattedResults.message,
          formattedResults.status
        );

        // Refresh applications list after successful undo
        await this.fetchTemplateApplications();
      } catch (err) {
        const errorMessage = err.response && err.response.data 
          ? JSON.stringify(err.response.data, null, 2)
          : err.message || 'Failed to undo template. Please try again.';
        
        this.actionError = 'Error: ' + errorMessage;
        console.error('Template undo error:', errorMessage);
        
        this.$alert(JSON.stringify(this.formatErrorForAlert(err, 'undo')));
      } finally {
        this.undoLoading = false;
      }
    },
    async deleteTemplate() {
      if (confirm('Are you sure you want to delete this template?')) {
        try {
          await managerService.configDelete('template', this.id, { aid: this.appId });
          await this.$store.dispatch('configFetch', 'template');
          this.$router.push(`/${this.appId}/templates`);
        } catch (err) {
          this.error = 'Failed to delete template';
          console.error(err);
        }
      }
    },
    handlePriorityChange(event, index) {
      const value = event.target.value;
      const param = this.template.parameters[index];
      
      if (value < 1) {
        param.priority = 1;
      } else {
        param.priority = value;
      }
    },
    async fetchTemplateApplications() {
      if (this.isNew) return;
      
      this.applicationsLoading = true;
      this.applicationsError = null;
      
      try {
        const applications = await templateService.getTemplateApplications(this.appId, null, this.id);
        console.log('Fetched applications:', applications);
        this.templateApplications = Array.isArray(applications) ? applications : [];
        console.log('Set templateApplications:', this.templateApplications);
      } catch (err) {
        this.applicationsError = 'Failed to fetch template applications';
        console.error('Error fetching template applications:', err);
      } finally {
        this.applicationsLoading = false;
      }
    },
    getAudienceName(audienceId) {
      const audience = this.audiences.find(a => a.id === audienceId);
      return audience ? audience.name : 'Unknown Audience';
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      return new Date(timestamp).toLocaleString();
    }
  },
  watch: {
    templates: {
      immediate: true,
      handler(newTemplates) {
        if (!this.isNew && newTemplates.length > 0) {
          this.fetchTemplate();
        }
      }
    }
  },
  async mounted() {
    // Parameters and audiences normally arrive via the app-wide fetchAllConfig, which is
    // fire-and-forget and skipped outright when the user isn't resolved yet. Fetch what this
    // view renders so an empty picker can't be mistaken for "this app has no parameters".
    const missing = [
      this.availableParameters.length ? null : 'parameter',
      this.audiences.length ? null : 'audience'
    ].filter(Boolean);

    try {
      await Promise.all(missing.map((type) => this.$store.dispatch('configFetch', type)));
      // Hand-off from the audience page. Runs after the fetch above, or a hard reload straight
      // onto this URL would copy from an empty parameter list and look like "no overrides".
      if (this.isNew && this.$route.query.fromAudience) {
        this.copyFromAudience(this.$route.query.fromAudience);
      }
    } catch (err) {
      this.error = 'Failed to load parameters and audiences';
      console.error('Error loading template form data:', err);
    }
  }
};
</script>

<style scoped>
.template-edit {
  min-height: 100vh;
  width: 100%; 
  padding: 20px;
  background-color: #1e1e2f;
}

/* Remove previous InfoBox styling */
/* Style for InfoBox spacing */
:deep(.header-info-box) {
  margin-bottom: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border-left: 4px solid #627cff;
}

.form-container {
  margin: 0;
  background: white;
  border-radius: 8px;
  padding: 24px;
}

.form-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #2c3e50;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-input:focus {
  border-color: #627cff;
  outline: none;
}

.parameters-list {
  margin-bottom: 16px;
}

.parameter-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
}

.parameter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.parameter-name {
  font-weight: 500;
  color: #2c3e50;
}

.parameter-description {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 8px;
}

.parameter-actions {
  display: flex;
  gap: 8px;
}

.edit-btn {
  color: #627cff;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
}

.remove-btn {
  color: #dc3545;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
}

.add-btn {
  background-color: #627cff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

/* Stacked: this panel lives in the narrow side column, where a select and a button
   side by side would squeeze the audience names down to nothing. */
.copy-from-audience-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.copy-from-audience-controls .form-input {
  width: 100%;
}

.copy-from-audience-controls .add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.copy-from-audience-message {
  margin-top: 8px;
  font-size: 13px;
  color: #a0a0b0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.btn-primary {
  background-color: #627cff;
  color: white;
  border: none;
  padding: 8px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 8px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1045;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #2c3e50;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #627cff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.error-message {
  background-color: #dc3545;
  color: white;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-parameters {
  text-align: center;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 4px;
  color: #6c757d;
  margin-bottom: 16px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.error-message {
  margin-top: 12px;
  padding: 8px 12px;
  background-color: #dc3545;
  color: white;
  border-radius: 4px;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #627cff;
  color: white;
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background-color: #4d63cc;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border: none;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
}

/* Add these styles for better integration with EnhancedAutocomplete */
:deep(.enhanced-autocomplete) {
  width: 100%;
}

:deep(.form-input) {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

:deep(.form-input:focus) {
  border-color: #627cff;
  outline: none;
}

.value-input-container {
  width: 100%;
}

.rich-text-input {
  width: 100%;
  min-height: 100px;
  resize: vertical;
  font-family: monospace;
  padding: 12px;
  line-height: 1.5;
}

.json-generator-container {
  width: 100%;
  margin-top: 8px;
}

.input-type-toggle {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.toggle-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.toggle-btn.active {
  background-color: #627cff;
  color: white;
  border-color: #627cff;
}

.toggle-btn:hover:not(.active) {
  background-color: #f5f5f5;
}

.parameter-value-container {
  position: relative;
}

.validation-error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.priority-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.priority-input {
  width: 50px;
  padding: 4px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.warning-content {
  margin: 20px 0;
}

.warning-list {
  max-height: 300px;
  overflow-y: auto;
  margin: 15px 0;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.warning-item {
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background-color: white;
}

.warning-header {
  margin-bottom: 8px;
  color: #2c3e50;
}

.warning-details {
  font-size: 14px;
  color: #6c757d;
}

.warning-details > div {
  margin: 4px 0;
}

.warning-message {
  margin-top: 15px;
  font-weight: 500;
  color: #2c3e50;
}

.applications-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #2c3e50;
}

.applications-list {
  max-height: 300px;
  overflow-y: auto;
}

.application-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 8px;
}

.application-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.application-audience {
  font-weight: 500;
  color: #2c3e50;
}

.application-time {
  font-size: 12px;
  color: #6c757d;
}

.application-details {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 14px;
}

.parameter-count {
  color: #6c757d;
}

.no-applications {
  text-align: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
  color: #6c757d;
}
</style> 